import type { components } from "@/api-schema";

export type VehicleValuationSelectionCandidate = components["schemas"]["VehicleValuationVehicleSelectionCandidateDto"];

export type VehicleValuationSelectionField = Exclude<keyof VehicleValuationSelectionCandidate, "candidateIndex">;

export type VehicleValuationSelectionColumnFormat =
  | "currency"
  | "displacement"
  | "drivetrain"
  | "powerKw"
  | "reimport"
  | "vehicleKind";

export interface VehicleValuationSelectionColumn {
  field: VehicleValuationSelectionField;
  format?: VehicleValuationSelectionColumnFormat;
  comparisonFields?: readonly VehicleValuationSelectionField[];
  translationKey: string;
}

export type VehicleValuationSelectionValues = Partial<Record<VehicleValuationSelectionField, string>>;

export type VehicleValuationSelectionState = "unchecked" | "explicit" | "inferred";

export interface VehicleValuationSelectionOption {
  candidate: VehicleValuationSelectionCandidate;
  value: string;
  selectionState: VehicleValuationSelectionState;
}

export interface VehicleValuationSelectionMatrixColumn {
  column: VehicleValuationSelectionColumn;
  options: VehicleValuationSelectionOption[];
}

const VEHICLE_VALUATION_SELECTION_COLUMNS: readonly VehicleValuationSelectionColumn[] = [
  { field: "manufacturer", translationKey: "fab:VehicleSelectionManufacturer" },
  { field: "modelFamily", translationKey: "fab:VehicleSelectionModelFamily" },
  { field: "baseModel", translationKey: "fab:VehicleSelectionModelGeneration" },
  { field: "model", translationKey: "fab:VehicleSelectionSalesDescription" },
  { field: "modelVariant", translationKey: "fab:VehicleSelectionModelVariant" },
  { field: "engineDescription", translationKey: "fab:VehicleSelectionEngine" },
  { field: "bodyDescription", translationKey: "fab:VehicleSelectionBody" },
  { field: "transmissionDescription", translationKey: "fab:VehicleSelectionTransmission" },
  { field: "marketIndexDescription", translationKey: "fab:VehicleSelectionMarketIndex" },
  { field: "kbaNumber", translationKey: "fab:VehicleSelectionKbaNumber" },
  {
    field: "vehicleKind",
    format: "vehicleKind",
    translationKey: "fab:VehicleSelectionVehicleKind",
  },
  { field: "bodyType", translationKey: "fab:VehicleSelectionBodyType" },
  { field: "fuelType", translationKey: "fab:VehicleSelectionFuelType" },
  {
    field: "drivetrain",
    format: "drivetrain",
    translationKey: "fab:VehicleSelectionDrivetrain",
  },
  {
    field: "enginePowerKw",
    format: "powerKw",
    comparisonFields: ["enginePowerKw", "enginePowerPs"],
    translationKey: "fab:VehicleSelectionEnginePower",
  },
  { field: "engineSize", format: "displacement", translationKey: "fab:VehicleSelectionEngineSize" },
  { field: "numberDoors", translationKey: "fab:VehicleSelectionNumberDoors" },
  { field: "numberGears", translationKey: "fab:VehicleSelectionNumberGears" },
  { field: "gearType", translationKey: "fab:VehicleSelectionGearType" },
  { field: "reimport", format: "reimport", translationKey: "fab:VehicleSelectionReimport" },
] as const;

const VEHICLE_VALUATION_STRUCTURED_TECHNICAL_COLUMNS = [
  {
    field: "engineDescription",
    fallbackFields: new Set<VehicleValuationSelectionField>(["enginePowerKw", "engineSize", "fuelType"]),
  },
  {
    field: "bodyDescription",
    fallbackFields: new Set<VehicleValuationSelectionField>(["bodyType", "numberDoors"]),
  },
  {
    field: "transmissionDescription",
    fallbackFields: new Set<VehicleValuationSelectionField>(["gearType", "numberGears"]),
  },
] as const;

/**
 * Uses the same whitespace equivalence users see in HTML so DAT values that differ
 * only by formatting share one option and one progressive-filter value.
 */
export function normalizeVehicleValuationSelectionText(value: string): string {
  return value.normalize("NFKC").trim().replace(/\s+/gu, " ").toLocaleLowerCase();
}

function normalizeComparisonValue(value: unknown): string {
  if (value === null || value === undefined || value === "") {
    return "__missing__";
  }

  if (typeof value === "string") {
    return normalizeVehicleValuationSelectionText(value);
  }

  return JSON.stringify(value);
}

function getVehicleValuationSelectionValue(
  candidate: VehicleValuationSelectionCandidate,
  column: VehicleValuationSelectionColumn,
): string {
  const comparisonFields = column.comparisonFields ?? [column.field];

  return comparisonFields.map((field) => normalizeComparisonValue(candidate[field])).join("|");
}

function matchesVehicleValuationSelection(
  candidate: VehicleValuationSelectionCandidate,
  columns: VehicleValuationSelectionColumn[],
  selectedValues: VehicleValuationSelectionValues,
): boolean {
  return columns.every((column) => {
    const selectedValue = selectedValues[column.field];
    return selectedValue === undefined || getVehicleValuationSelectionValue(candidate, column) === selectedValue;
  });
}

/**
 * A selection column is useful only when at least two candidates have different values.
 * Missing values participate in the comparison because "known vs unknown" can also distinguish
 * two otherwise similar DAT records.
 */
export function getVehicleValuationSelectionColumns(
  candidates: VehicleValuationSelectionCandidate[],
): VehicleValuationSelectionColumn[] {
  const distinguishingColumns = VEHICLE_VALUATION_SELECTION_COLUMNS.filter((column) => {
    const distinctValues = new Set(candidates.map((candidate) => getVehicleValuationSelectionValue(candidate, column)));

    return distinctValues.size >= 2;
  });

  let visibleColumns = distinguishingColumns;

  for (const structuredColumn of VEHICLE_VALUATION_STRUCTURED_TECHNICAL_COLUMNS) {
    const hasCompleteStructuredValues = candidates.every((candidate) => {
      const value = candidate[structuredColumn.field];
      return typeof value === "string" && Boolean(value.trim());
    });
    const isStructuredColumnDistinguishing = distinguishingColumns.some(
      ({ field }) => field === structuredColumn.field,
    );

    if (hasCompleteStructuredValues && isStructuredColumnDistinguishing) {
      visibleColumns = visibleColumns.filter(({ field }) => !structuredColumn.fallbackFields.has(field));
    }
  }

  return visibleColumns;
}

/**
 * Returns the candidates that are compatible with every currently selected matrix value.
 */
export function getMatchingVehicleValuationSelectionCandidates(
  candidates: VehicleValuationSelectionCandidate[],
  columns: VehicleValuationSelectionColumn[],
  selectedValues: VehicleValuationSelectionValues,
): VehicleValuationSelectionCandidate[] {
  return candidates.filter((candidate) => matchesVehicleValuationSelection(candidate, columns, selectedValues));
}

export function getSelectedVehicleValuationCandidate(
  matchingCandidates: VehicleValuationSelectionCandidate[],
  explicitCandidateIndex: number | null,
  columns: VehicleValuationSelectionColumn[],
): VehicleValuationSelectionCandidate | null {
  if (hasDistinguishingVehicleValuationSelectionValue(matchingCandidates, columns)) {
    return null;
  }

  return matchingCandidates.length === 1
    ? matchingCandidates[0]
    : (matchingCandidates.find((candidate) => candidate.candidateIndex === explicitCandidateIndex) ?? null);
}

export function toggleVehicleValuationSelectionValue(
  explicitSelectedValues: VehicleValuationSelectionValues,
  field: VehicleValuationSelectionField,
  option: Pick<VehicleValuationSelectionOption, "value" | "selectionState">,
): VehicleValuationSelectionValues {
  if (option.selectionState === "inferred") {
    return explicitSelectedValues;
  }

  if (explicitSelectedValues[field] === option.value) {
    const nextValues = { ...explicitSelectedValues };
    delete nextValues[field];
    return nextValues;
  }

  return { ...explicitSelectedValues, [field]: option.value };
}

/**
 * Builds one distinct option list per column from the candidates that remain
 * compatible with the current selection.
 */
export function getVehicleValuationSelectionMatrix(
  candidates: VehicleValuationSelectionCandidate[],
  columns: VehicleValuationSelectionColumn[],
  explicitSelectedValues: VehicleValuationSelectionValues,
): VehicleValuationSelectionMatrixColumn[] {
  const matchingCandidates = getMatchingVehicleValuationSelectionCandidates(
    candidates,
    columns,
    explicitSelectedValues,
  );

  return columns.map((column) => {
    const optionsByValue = new Map<string, VehicleValuationSelectionCandidate>();

    for (const candidate of matchingCandidates) {
      const value = getVehicleValuationSelectionValue(candidate, column);
      if (!optionsByValue.has(value)) {
        optionsByValue.set(value, candidate);
      }
    }

    return {
      column,
      options: Array.from(optionsByValue, ([value, candidate]) => ({
        candidate,
        value,
        selectionState:
          explicitSelectedValues[column.field] === value
            ? "explicit"
            : optionsByValue.size === 1
              ? "inferred"
              : "unchecked",
      })),
    };
  });
}

/**
 * Tells the view whether the remaining candidates can still be distinguished by
 * an attribute column. If not, the matrix can expose a candidate-number fallback
 * so identical DAT records remain individually selectable.
 */
export function hasDistinguishingVehicleValuationSelectionValue(
  candidates: VehicleValuationSelectionCandidate[],
  columns: VehicleValuationSelectionColumn[],
): boolean {
  return columns.some((column) => {
    const values = new Set(candidates.map((candidate) => getVehicleValuationSelectionValue(candidate, column)));
    return values.size >= 2;
  });
}
